/**
 * Сохранение резервной копии.
 *
 * В браузере файл отдаётся ссылкой — этого достаточно. В приложении так не
 * работает: ссылка на blob внутри WebView никуда не ведёт, скачивание молча не
 * происходит, и человек нажимает кнопку, получает бодрое сообщение об успехе и
 * остаётся без копии. Это худший вид неисправности — приложение уверяет, что
 * данные сохранены, а их нет.
 *
 * Поэтому на устройстве копия пишется в файл через системный доступ к
 * хранилищу и сразу предлагается к отправке: в облако, в мессенджер, куда
 * человек привык складывать важное. Файл при этом остаётся в папке приложения.
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

import { writeBackupToFiles } from './backup-files';
import { serializeBackup } from './backup-payload';
import type { Backup } from './export-data';
import { backupFileName } from './export-data';

/** Где оказалась копия — от этого зависит, что сказать человеку. */
export type BackupTarget = 'download' | 'file' | 'shared';

export interface SaveResult {
  target: BackupTarget;
  /** Путь к файлу на устройстве. В браузере пути нет. */
  path?: string;
}

/** Работаем внутри приложения, а не во вкладке браузера. */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Отдаёт копию файлом в браузере.
 *
 * Ссылка на blob живёт до конца сессии, поэтому освобождается сразу: копия
 * может весить мегабайты, и держать её в памяти после сохранения незачем.
 */
function saveInBrowser(backup: Backup, content: string): SaveResult {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = backupFileName(backup.exportedAt);
  link.click();

  URL.revokeObjectURL(url);
  return { target: 'download' };
}

/**
 * Пишет копию в файл и ничего не спрашивает.
 *
 * Нужна автоматической копии: она снимается сама при первом за сутки открытии,
 * и диалог отправки, всплывающий на ровном месте, превратил бы тихую страховку
 * в ежедневную помеху.
 */
export async function writeBackupFile(backup: Backup, content: string): Promise<string> {
  return writeBackupToFiles(backup, content);
}

/**
 * Пишет копию в файл на устройстве и сразу предлагает отправить её дальше.
 *
 * Диалог отправки — не украшение: файл на телефоне не переживёт потерю
 * телефона, а смысл копии как раз в этом. Если человек диалог закрыл, файл всё
 * равно остался — об этом и сообщаем.
 */
async function saveOnDevice(backup: Backup, content: string): Promise<SaveResult> {
  const written = { uri: await writeBackupFile(backup, content) };

  try {
    await Share.share({
      title: 'Резервная копия Kairo',
      text: `Копия данных от ${backup.exportedAt.slice(0, 10)}`,
      url: written.uri,
      dialogTitle: 'Куда сохранить копию',
    });
    return { target: 'shared', path: written.uri };
  } catch {
    // Отказ от отправки — обычное действие, а не сбой: файл уже записан.
    return { target: 'file', path: written.uri };
  }
}

/**
 * Сохраняет копию — уже закрытую паролем, если человек его задал.
 *
 * Шифрование решается здесь, в одной точке на все способы сохранения.
 */
export async function saveBackup(userId: string, backup: Backup): Promise<SaveResult> {
  const content = await serializeBackup(userId, backup);

  return isNativeApp() ? saveOnDevice(backup, content) : saveInBrowser(backup, content);
}

/** Что сказать человеку после сохранения. */
export function describeSaveResult(result: SaveResult, records: number): string {
  if (result.target === 'download') return `Копия сохранена: ${records} записей`;
  if (result.target === 'shared') return `Копия отправлена: ${records} записей`;
  return `Копия сохранена в файлы устройства: ${records} записей`;
}

/**
 * Аппаратная кнопка «назад».
 *
 * Без обработчика она закрывает приложение из любого места — то есть выход из
 * шторки или из раздела означал выход целиком. Здесь она делает то же, что и
 * стрелка в интерфейсе: возвращает на шаг назад, а на корневом экране
 * сворачивает приложение, как принято в Android.
 */
export function watchHardwareBack(): () => void {
  if (!isNativeApp()) return () => undefined;

  const handle = App.addListener('backButton', ({ canGoBack }) => {
    // Открытая шторка забирает нажатие себе. Она уже умеет закрываться по
    // Escape — тот же путь, что и с внешней клавиатуры, поэтому отдельной
    // договорённости между шторкой и оболочкой не нужно.
    if (document.querySelector('[data-sheet-open]')) {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
      return;
    }

    if (canGoBack) window.history.back();
    else void App.minimizeApp();
  });

  return () => {
    void handle.then((listener) => listener.remove());
  };
}
